import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, ScanBarcode, X } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { apiError, fmtDate } from '../lib/industria'
import { ORDER_STATUS_LABEL, type OrderDetail, type OrderRow } from '../lib/producao'

const selectClass = 'h-12 w-full rounded-xl border border-gray-300 bg-white px-3 text-base text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'
const inputClass = 'h-14 w-full rounded-xl border border-gray-300 px-4 text-lg focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

type ScannedVolume = {
  id: string; code: string; qtyCurrent: number; status: string
  materialId: string; materialCode: string; materialName: string; unit: string
  lotNumber: string | null; expiryDate: string | null; ownerName: string | null
  locationCode: string | null
}

// Tela de chão de fábrica: um dedo, uma leitura, um número. Bipar o volume
// resolve material, lote e dono — o operador só informa a quantidade.
export function IndustriaApontamentoPage() {
  const queryClient = useQueryClient()
  const scanRef = useRef<HTMLInputElement>(null)

  const [orderId, setOrderId] = useState('')
  const [scan, setScan] = useState('')
  const [volume, setVolume] = useState<ScannedVolume | null>(null)
  const [quantity, setQuantity] = useState('')
  const [kind, setKind] = useState<'consumo' | 'perda'>('consumo')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)

  const { data: orders = [] } = useQuery<OrderRow[]>({
    queryKey: ['producao', 'orders', 'appointable'],
    queryFn: async () => (await api.get('/production-orders')).data,
  })

  const { data: order } = useQuery<OrderDetail>({
    queryKey: ['producao', 'order', orderId],
    queryFn: async () => (await api.get(`/production-orders/${orderId}`)).data,
    enabled: !!orderId,
  })

  const open = orders.filter(o =>
    ['released', 'in_progress', 'reported', 'pending_approval'].includes(o.status))

  useEffect(() => { scanRef.current?.focus() }, [orderId])

  async function resolveVolume(code: string) {
    setError(null)
    setResolving(true)
    try {
      const { data } = await api.get(`/material-volumes/by-code/${encodeURIComponent(code.trim())}`)
      setVolume(data)
      setScan('')
    } catch (e) {
      setVolume(null)
      setError(apiError(e, `Volume ${code} não encontrado`))
    } finally {
      setResolving(false)
    }
  }

  const consumeMutation = useMutation({
    mutationFn: async () => (await api.post(`/production-orders/${orderId}/consume`, {
      volumeId: volume?.id,
      quantity: Number(quantity),
      kind,
      reason: reason.trim() || null,
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producao', 'order', orderId] })
      queryClient.invalidateQueries({ queryKey: ['industria', 'materials'] })
      setOk(`${quantity} ${volume?.unit ?? ''} de ${volume?.materialName ?? ''} apontado`)
      setVolume(null)
      setQuantity('')
      setReason('')
      setKind('consumo')
      setTimeout(() => setOk(null), 4000)
      scanRef.current?.focus()
    },
    onError: (e) => setError(apiError(e, 'Não foi possível apontar')),
  })

  const item = order?.items.find(i => i.materialId === volume?.materialId)
  const notInRecipe = !!volume && !!order && !item

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Apontamento</h2>
        <p className="text-sm text-gray-500">Bipe o volume, informe a quantidade. O resto vem do lote.</p>
      </div>

      <Card>
        <CardContent className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Ordem</label>
            <select className={selectClass} value={orderId} onChange={e => { setOrderId(e.target.value); setVolume(null) }}>
              <option value="">Selecione a ordem...</option>
              {open.map(o => (
                <option key={o.id} value={o.id}>
                  {o.number} · {o.productSku} · {o.qtyPlanned} un · {ORDER_STATUS_LABEL[o.status]}
                </option>
              ))}
            </select>
            {open.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Nenhuma ordem liberada. Libere uma em Ordens de produção.
              </p>
            )}
          </div>

          {!!order && (
            <div className="rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600">
              <span className="font-medium text-gray-900">{order.productName}</span>
              {order.lotNumber && <span className="ml-2 font-mono text-xs">lote {order.lotNumber}</span>}
              <span className="ml-2 text-xs">{order.qtyPlanned.toLocaleString('pt-BR')} un planejadas</span>
            </div>
          )}
        </CardContent>
      </Card>

      {ok && (
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
          <Check className="h-4 w-4" /> {ok}
        </div>
      )}
      {error && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X className="h-4 w-4" /></button>
        </div>
      )}

      {!!orderId && (
        <Card>
          <CardContent className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Volume</label>
              <div className="relative">
                <ScanBarcode className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  ref={scanRef}
                  className={`${inputClass} pl-12 font-mono`}
                  placeholder="WS-M-000123"
                  value={scan}
                  autoComplete="off"
                  onChange={e => setScan(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && scan.trim()) resolveVolume(scan) }}
                />
                {resolving && (
                  <Loader2 className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 animate-spin text-gray-400" />
                )}
              </div>
            </div>

            {volume && (
              <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
                <p className="text-base font-semibold text-gray-900">{volume.materialName}</p>
                <p className="font-mono text-xs text-gray-500">
                  {volume.materialCode} · {volume.code}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                  <Badge variant="info">lote {volume.lotNumber ?? '—'}</Badge>
                  <Badge>{volume.qtyCurrent.toLocaleString('pt-BR')} {volume.unit} no volume</Badge>
                  {volume.ownerName && <Badge>{volume.ownerName}</Badge>}
                  {volume.locationCode && <span className="font-mono">{volume.locationCode}</span>}
                  {volume.expiryDate && <span>val. {fmtDate(volume.expiryDate)}</span>}
                </div>
                {item && (
                  <p className="mt-2 text-sm text-gray-700">
                    Falta apontar <span className="font-mono font-semibold">{item.remaining.toLocaleString('pt-BR')}</span>{' '}
                    de {item.need.toLocaleString('pt-BR')} previstas
                    {item.overScrapLimit && (
                      <span className="ml-2 font-semibold text-red-600">perda acima do teto</span>
                    )}
                  </p>
                )}
                {notInRecipe && (
                  <p className="mt-2 rounded-lg bg-red-50 px-2 py-1 text-sm text-red-700">
                    Este material não está na ficha desta ordem — a API vai recusar.
                  </p>
                )}
              </div>
            )}

            {volume && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setKind('consumo')}
                    className={`h-12 rounded-xl border text-sm font-medium transition-colors ${
                      kind === 'consumo'
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    Consumo
                  </button>
                  <button
                    type="button"
                    onClick={() => setKind('perda')}
                    className={`h-12 rounded-xl border text-sm font-medium transition-colors ${
                      kind === 'perda'
                        ? 'border-red-300 bg-red-50 text-red-700'
                        : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    Perda
                  </button>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Quantidade</label>
                  <input
                    className={inputClass}
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                  />
                </div>

                {kind === 'perda' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Motivo</label>
                    <input
                      className={inputClass}
                      placeholder="vidro quebrado, erro de rotulagem, setup"
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Perda acima do teto da ficha não trava a linha — trava o encerramento até um admin aprovar.
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() => { setVolume(null); setQuantity(''); scanRef.current?.focus() }}
                  >
                    Trocar volume
                  </Button>
                  <Button
                    className="flex-1"
                    size="lg"
                    loading={consumeMutation.isPending}
                    disabled={!quantity || Number(quantity) <= 0 || (kind === 'perda' && !reason.trim())}
                    onClick={() => { setError(null); consumeMutation.mutate() }}
                  >
                    Apontar
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {!!order && order.consumptions.length > 0 && (
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">Últimos apontamentos</span>
          </CardHeader>
          <CardContent className="space-y-2">
            {order.consumptions.slice(0, 8).map(c => (
              <div key={c.id} className="flex items-center justify-between border-b border-gray-50 pb-2 text-sm last:border-0">
                <div>
                  <span className="text-gray-900">{c.materialName}</span>
                  <span className="block font-mono text-xs text-gray-400">
                    {c.volumeCode ?? c.lotNumber ?? '—'}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-mono font-medium text-gray-900">
                    {c.quantity.toLocaleString('pt-BR')}
                  </span>
                  {c.kind === 'perda' && <Badge variant="danger" className="ml-2">perda</Badge>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
